import React, { useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import ReminderNotificationBell from './ReminderNotificationBell';
import BrandLogo from './BrandLogo';

export default function Topbar({ open, setOpen }) {
  const navigate = useNavigate();
  const { user, roleLabel, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const displayName = user?.name || user?.email || 'Account';
  const initial = displayName.slice(0, 1).toUpperCase();

  const closeMenu = () => setAnchorEl(null);

  const goToProfile = () => {
    closeMenu();
    navigate('/profile');
  };

  const handleLogout = () => {
    closeMenu();
    logout();
  };

  return (
    <AppBar
      position="static"
      sx={{
        zIndex: theme => theme.zIndex.drawer + 1,
        background: 'linear-gradient(90deg, #001f3f 0%, #00509e 100%)', // navy gradient
        boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
      }}
    >
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', px: { xs: 1, sm: 2 } }}>
        {/* Left section: menu + title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            color="inherit"
            onClick={() => setOpen(!open)}
            sx={{
              mr: { xs: 0.5, sm: 1 },
              backgroundColor: 'rgba(255,255,255,0.08)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' }
            }}
            aria-label="Toggle sidebar"
          >
            <MenuIcon />
          </IconButton>
          <BrandLogo
            height={44}
            showText
            textColor="#ffffff"
            subtitleColor="rgba(255,255,255,0.74)"
            logoGlow
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          />
          <BrandLogo height={38} compact showText={false} logoGlow sx={{ display: { xs: 'flex', sm: 'none' } }} />
        </Box>

        {/* Right section: notifications + user */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
          <ReminderNotificationBell />
          <Button
            color="inherit"
            onClick={(event) => setAnchorEl(event.currentTarget)}
            startIcon={<Avatar sx={{ width: 26, height: 26, bgcolor: 'rgba(255,255,255,0.22)', color: 'white', fontSize: 13, fontWeight: 900 }}>{initial}</Avatar>}
            endIcon={<AccountCircle sx={{ fontSize: { xs: 18, sm: 20 } }} />}
            sx={{ textTransform: 'none', fontWeight: 'bold', display: { xs: 'none', sm: 'flex' }, borderRadius: 999, px: 1.4 }}
          >
            {displayName}
          </Button>
          <IconButton color="inherit" onClick={(event) => setAnchorEl(event.currentTarget)} sx={{ display: { xs: 'flex', sm: 'none' } }}>
            <AccountCircle sx={{ fontSize: 20 }} />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={closeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 260,
                borderRadius: 2,
                boxShadow: '0 14px 34px rgba(15,23,42,0.22)',
                overflow: 'hidden',
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc' }}>
              <Typography sx={{ fontWeight: 900, color: '#001f3f' }} noWrap>{displayName}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap>{user?.email || ''}</Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#1976d2', fontWeight: 800 }}>{roleLabel}</Typography>
            </Box>
            <Divider />
            <MenuItem onClick={goToProfile}>
              <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogout} sx={{ color: '#c62828', fontWeight: 800 }}>
              <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
              Log Out
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
